import { useState, useEffect } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Receipt, Loader2, Users, Euro, Calendar, Zap, AlertTriangle, CheckCircle } from "lucide-react";
import { BillingPeriod } from "@/hooks/useFinanceData";
import { supabase } from "@/integrations/supabase/client";

interface InvoiceGeneratorProps {
  activePeriod: BillingPeriod | undefined;
  isGenerating: boolean;
  onGenerateInvoices: () => void;
}

export const InvoiceGenerator = ({ 
  activePeriod, 
  isGenerating, 
  onGenerateInvoices 
}: InvoiceGeneratorProps) => {
  const [showPreview, setShowPreview] = useState(false);
  const [previewData, setPreviewData] = useState<any>(null);
  const [loadingPreview, setLoadingPreview] = useState(false);

  const generatePreview = async () => {
    if (!activePeriod) return;
    
    try {
      setLoadingPreview(true);
      
      console.log('🔍 [Preview] Generando vista previa para período:', activePeriod.name);
      
      // Verificar facturas existentes
      const { data: existingBilling } = await supabase
        .from('student_billing')
        .select('student_id, activity_id')
        .eq('billing_period_id', activePeriod.id);

      const existingCount = existingBilling?.length || 0;
      console.log('📋 [Preview] Facturas existentes:', existingCount);

      // Obtener inscripciones activas CON default_billing_type (igual que la generación real)
      const { data: enrollments } = await supabase
        .from('student_activities')
        .select(`
          student_id,
          activity_id,
          billing_type_override,
          activities(id, name),
          students(id, name, default_billing_type)
        `)
        .eq('status', 'active');

      console.log('👥 [Preview] Inscripciones activas:', enrollments?.length);

      // Obtener precios de actividades (ambos tipos)
      const { data: prices } = await supabase
        .from('activity_prices')
        .select('*')
        .eq('is_active', true);

      console.log('💰 [Preview] Precios activos:', prices?.length);

      // Crear mapas de precios por tipo de facturación
      const sessionPricesMap = new Map();
      const monthlyPricesMap = new Map();
      
      prices?.forEach(p => {
        if (p.billing_type_target === 'per_session') {
          sessionPricesMap.set(p.activity_id, p);
        } else if (p.billing_type_target === 'monthly') {
          monthlyPricesMap.set(p.activity_id, p);
        }
      });

      // Obtener asistencias del período
      const { data: attendances } = await supabase
        .from('attendances')
        .select('activity_id, student_id')
        .gte('date', activePeriod.start_date)
        .lte('date', activePeriod.end_date)
        .eq('status', 'present');

      const attendancesByKey = new Map<string, number>();
      attendances?.forEach((att: any) => {
        const key = `${att.student_id}-${att.activity_id}`;
        attendancesByKey.set(key, (attendancesByKey.get(key) || 0) + 1);
      });

      console.log('📊 [Preview] Asistencias agrupadas:', attendancesByKey.size);

      // Aplicar LA MISMA LÓGICA que en la generación real (con jerarquía corregida)
      const existingKeys = new Set(existingBilling?.map(b => `${b.student_id}-${b.activity_id}`) || []);
      let toProcess = 0;
      let byType = { per_session: 0, monthly: 0 };
      let withAttendances = 0;
      let totalAmount = 0;

      enrollments?.forEach((enrollment: any) => {
        const key = `${enrollment.student_id}-${enrollment.activity_id}`;
        
        // Omitir si ya existe factura
        if (existingKeys.has(key)) {
          return;
        }

        // DETERMINAR TIPO DE FACTURACIÓN EFECTIVO CON JERARQUÍA CORREGIDA
        const studentDefaultBilling = enrollment.students?.default_billing_type || 'per_session';
        const effectiveBillingType = enrollment.billing_type_override || studentDefaultBilling;
        
        console.log(`🔍 [Preview] ${enrollment.students?.name} en ${enrollment.activities?.name}:`);
        console.log(`   - Override: ${enrollment.billing_type_override || 'ninguno'}`);
        console.log(`   - Default estudiante: ${studentDefaultBilling}`);
        console.log(`   - Tipo efectivo: ${effectiveBillingType}`);

        // Obtener precio según el tipo de facturación efectivo
        const price = effectiveBillingType === 'per_session' 
          ? sessionPricesMap.get(enrollment.activity_id)
          : monthlyPricesMap.get(enrollment.activity_id);

        // Validar que existe precio para el tipo de facturación efectivo
        if (!price) {
          console.log(`❌ [Preview] Sin precio ${effectiveBillingType} para ${enrollment.activities?.name}`);
          return;
        }

        // LÓGICA DUAL DE FACTURACIÓN (igual que en generación real)
        if (effectiveBillingType === 'per_session') {
          // FACTURACIÓN POR DÍA: solo si hay asistencias
          const sessionsAttended = attendancesByKey.get(key) || 0;
          if (sessionsAttended === 0) {
            console.log(`⏭️ [Preview] ${enrollment.students?.name}: sin asistencias, omitiendo`);
            return;
          }
          
          toProcess++;
          byType.per_session++;
          withAttendances++;
          totalAmount += sessionsAttended * price.price_amount;
          
          console.log(`✅ [Preview] ${enrollment.students?.name}: ${sessionsAttended} sesiones × €${price.price_amount} = €${sessionsAttended * price.price_amount}`);
        } else {
          // FACTURACIÓN MENSUAL: siempre se procesa
          toProcess++;
          byType.monthly++;
          totalAmount += price.price_amount;
          
          console.log(`✅ [Preview] ${enrollment.students?.name}: cuota mensual €${price.price_amount}`);
        }
      });

      setPreviewData({
        existingCount,
        toProcess,
        totalEnrollments: enrollments?.length || 0,
        byType,
        withAttendances,
        attendancesTotal: attendances?.length || 0,
        totalAmount: totalAmount
      });
      
      console.log('📋 [Preview] Resultado final:', {
        existingCount,
        toProcess,
        byType,
        totalAmount
      });
      
      setShowPreview(true);
    } catch (error) {
      console.error('❌ [Preview] Error generando vista previa:', error);
    } finally {
      setLoadingPreview(false);
    }
  };

  const handleGenerateClick = () => {
    setShowPreview(false);
    onGenerateInvoices();
  };

  return (
    <>
      <Card className="shadow-card border-l-4 border-l-primary bg-gradient-to-r from-primary/5 to-secondary/5">
        <CardHeader>
          <CardTitle className="flex items-center gap-3">
            <Receipt className="w-6 h-6 text-primary" />
            <div>
              <span className="text-xl">Generador de Facturas</span>
              {activePeriod && (
                <Badge variant="outline" className="ml-2">
                  <Calendar className="w-3 h-3 mr-1" />
                  {activePeriod.name}
                </Badge>
              )}
            </div>
          </CardTitle>
        </CardHeader>
        <CardContent>
          {activePeriod ? (
            <div className="space-y-4">
              <div className="flex items-center justify-between p-4 bg-white/50 dark:bg-slate-800/50 rounded-lg border">
                <div className="space-y-1">
                  <p className="font-medium">Período activo: {activePeriod.name}</p>
                  <p className="text-sm text-muted-foreground">
                    📅 {activePeriod.start_date} → {activePeriod.end_date}
                  </p>
                </div>
                <div className="flex gap-2">
                  <Button 
                    variant="outline"
                    onClick={generatePreview}
                    disabled={isGenerating || loadingPreview}
                  >
                    {loadingPreview ? (
                      <>
                        <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                        Analizando...
                      </>
                    ) : (
                      <>
                        👁️ Vista Previa
                      </>
                    )}
                  </Button>
                  <Button 
                    onClick={onGenerateInvoices}
                    disabled={isGenerating}
                    size="lg"
                    className="min-w-[160px]"
                  >
                    {isGenerating ? (
                      <>
                        <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                        Generando...
                      </>
                    ) : (
                      <>
                        <Receipt className="w-4 h-4 mr-2" />
                        Generar Facturas
                      </>
                    )}
                  </Button>
                </div>
              </div>
              
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4 text-sm">
                <div className="p-3 bg-blue-50 dark:bg-blue-950/20 rounded-lg border border-blue-200 dark:border-blue-800 text-center">
                  <Zap className="w-5 h-5 mx-auto mb-1 text-blue-600" />
                  <p className="font-medium text-blue-900 dark:text-blue-100">Facturación por Día</p>
                  <p className="text-xs text-blue-600 dark:text-blue-400">Se factura según asistencias</p>
                </div>
                <div className="p-3 bg-green-50 dark:bg-green-950/20 rounded-lg border border-green-200 dark:border-green-800 text-center">
                  <Calendar className="w-5 h-5 mx-auto mb-1 text-green-600" />
                  <p className="font-medium text-green-900 dark:text-green-100">Cuotas Mensuales</p>
                  <p className="text-xs text-green-600 dark:text-green-400">Precio fijo independiente</p>
                </div>
                <div className="p-3 bg-purple-50 dark:bg-purple-950/20 rounded-lg border border-purple-200 dark:border-purple-800 text-center">
                  <CheckCircle className="w-5 h-5 mx-auto mb-1 text-purple-600" />
                  <p className="font-medium text-purple-900 dark:text-purple-100">Anti-duplicados</p>
                  <p className="text-xs text-purple-600 dark:text-purple-400">Prevención automática</p>
                </div>
              </div>
            </div>
          ) : (
            <div className="p-4 bg-amber-50 dark:bg-amber-950/20 border border-amber-200 dark:border-amber-800 rounded-lg">
              <div className="flex items-center gap-2 mb-2">
                <AlertTriangle className="w-5 h-5 text-amber-600" />
                <span className="font-medium text-amber-900 dark:text-amber-100">Sin período activo</span>
              </div>
              <p className="text-sm text-amber-700 dark:text-amber-300">
                Necesitas activar un período de facturación antes de generar facturas. Ve a la pestaña "Configuración" para gestionar períodos.
              </p>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Dialog de Vista Previa */}
      <Dialog open={showPreview} onOpenChange={setShowPreview}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              👁️ Vista Previa de Facturación
              <Badge variant="outline">{activePeriod?.name}</Badge>
            </DialogTitle>
          </DialogHeader>
          
          {previewData && (
            <div className="space-y-6">
              {/* Resumen principal */}
              <div className="grid grid-cols-2 gap-4">
                <div className="p-4 bg-blue-50 dark:bg-blue-950/20 rounded-lg border border-blue-200 dark:border-blue-800">
                  <div className="flex items-center gap-2 mb-2">
                    <CheckCircle className="w-5 h-5 text-blue-600" />
                    <span className="font-medium text-blue-900 dark:text-blue-100">Ya Facturadas</span>
                  </div>
                  <p className="text-2xl font-bold text-blue-700 dark:text-blue-300">{previewData.existingCount}</p>
                  <p className="text-xs text-blue-600 dark:text-blue-400">Se omitirán automáticamente</p>
                </div>
                
                <div className="p-4 bg-green-50 dark:bg-green-950/20 rounded-lg border border-green-200 dark:border-green-800">
                  <div className="flex items-center gap-2 mb-2">
                    <Receipt className="w-5 h-5 text-green-600" />
                    <span className="font-medium text-green-900 dark:text-green-100">Nuevas a Generar</span>
                  </div>
                  <p className="text-2xl font-bold text-green-700 dark:text-green-300">{previewData.toProcess}</p>
                  <p className="text-xs text-green-600 dark:text-green-400">Facturas pendientes</p>
                </div>
              </div>

              {/* Monto total estimado */}
              {previewData.totalAmount > 0 && (
                <div className="p-4 bg-purple-50 dark:bg-purple-950/20 rounded-lg border border-purple-200 dark:border-purple-800">
                  <div className="flex items-center gap-2 mb-2">
                    <Euro className="w-5 h-5 text-purple-600" />
                    <span className="font-medium text-purple-900 dark:text-purple-100">Total Estimado</span>
                  </div>
                  <p className="text-2xl font-bold text-purple-700 dark:text-purple-300">€{previewData.totalAmount.toFixed(2)}</p>
                  <p className="text-xs text-purple-600 dark:text-purple-400">Ingresos a generar</p>
                </div>
              )}

              {/* Desglose por tipo */}
              <div className="space-y-3">
                <h4 className="font-medium flex items-center gap-2">
                  <Euro className="w-4 h-4" />
                  Desglose por Tipo de Facturación
                </h4>
                <div className="grid grid-cols-2 gap-3">
                  <div className="p-3 bg-amber-50 dark:bg-amber-950/20 rounded border border-amber-200 dark:border-amber-800">
                    <div className="flex items-center gap-2">
                      <Zap className="w-4 h-4 text-amber-600" />
                      <span className="font-medium">Por Día</span>
                    </div>
                    <p className="text-lg font-bold text-amber-700 dark:text-amber-300">{previewData.byType.per_session}</p>
                    <p className="text-xs text-amber-600 dark:text-amber-400">
                      {previewData.withAttendances} con asistencias
                    </p>
                  </div>
                  <div className="p-3 bg-blue-50 dark:bg-blue-950/20 rounded border border-blue-200 dark:border-blue-800">
                    <div className="flex items-center gap-2">
                      <Calendar className="w-4 h-4 text-blue-600" />
                      <span className="font-medium">Cuota Mensual</span>
                    </div>
                    <p className="text-lg font-bold text-blue-700 dark:text-blue-300">{previewData.byType.monthly}</p>
                    <p className="text-xs text-blue-600 dark:text-blue-400">Precio fijo</p>
                  </div>
                </div>
              </div>

              {/* Estadísticas adicionales */}
              <div className="p-3 bg-muted/50 rounded-lg">
                <h5 className="font-medium mb-2">📊 Estadísticas del período</h5>
                <div className="grid grid-cols-2 gap-4 text-sm">
                  <div>
                    <span className="text-muted-foreground">Total inscripciones:</span>
                    <span className="ml-2 font-medium">{previewData.totalEnrollments}</span>
                  </div>
                  <div>
                    <span className="text-muted-foreground">Asistencias registradas:</span>
                    <span className="ml-2 font-medium">{previewData.attendancesTotal}</span>
                  </div>
                </div>
              </div>

              {previewData.toProcess === 0 && (
                <div className="p-3 bg-amber-50 dark:bg-amber-950/20 rounded border border-amber-200 dark:border-amber-800">
                  <p className="text-sm text-amber-700 dark:text-amber-300">
                    ℹ️ No hay nuevas facturas para generar. Todas las combinaciones estudiante-actividad ya están facturadas para este período.
                  </p>
                </div>
              )}

              <div className="flex justify-end gap-2">
                <Button variant="outline" onClick={() => setShowPreview(false)}>
                  Cancelar
                </Button>
                <Button 
                  onClick={handleGenerateClick}
                  disabled={previewData.toProcess === 0}
                >
                  <Receipt className="w-4 h-4 mr-2" />
                  Generar {previewData.toProcess} Facturas
                </Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </>
  );
};
